import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { DataTestId, TextKey } from '@mews-commander/enums';
import { LocalizedText, lt } from '@mews/framework';
import { Banner, Button, Stack } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { WithBillId } from '@mews-commander/types';
import { fieldNames, ReasonField } from '../form';
import { BillDetails } from '../billDetails';
import { CreditNoteCounterSelect } from '../form/CreditNoteCounterSelect';
import { useGetCreditNoteCounters, useIsCreditNoteCountersEnabled } from '../../../counters/utils';
import { findCounterByBillType } from '../../../counters/helpers';
import { useDialogControls } from './useDialogControls';
import { ActionLogInfo } from './ActionLogInfo';

export const ReinstateDialog = ({ billId }: WithBillId) => {
    const { bill, handleClose, isLoading, submit, renderFormContent } = useDialogControls({ billId });

    const showCreditNoteCounterSelect = useIsCreditNoteCountersEnabled();
    const creditNoteCounters = useGetCreditNoteCounters();

    const hasCreditNoteCounters = pipe(
        creditNoteCounters,
        array.some(findCounterByBillType(option.fromNullable(bill?.type)))
    );

    return (
        <FloatingPanel
            active
            size="small"
            actions={[
                {
                    children: lt('BillReissueSubmitButtonLabel'),
                    onClick: submit,
                    variant: 'primary',
                    disabled: showCreditNoteCounterSelect && !hasCreditNoteCounters,
                    size: 'medium',
                    'data-test-id': DataTestId.BillReinstateFormSubmitButton,
                    type: 'submit',
                },
            ]}
            title={lt('ReissueAsOpenBill')}
            footerDetail={
                <Button onClick={handleClose} size="medium" type="submit">
                    {lt('Cancel')}
                </Button>
            }
            onClose={handleClose}
            loading={isLoading}
        >
            <Stack spacing="300" vertical>
                <Banner
                    variant="info"
                    primaryText={
                        <LocalizedText
                            textKey={TextKey.ChangeBillingDetailsMainBannerDescription}
                            substitutes={{ documentName: bill?.name ?? billId }}
                        />
                    }
                />
                {renderFormContent(
                    <Stack vertical spacing="300">
                        <ReasonField />
                        {showCreditNoteCounterSelect ? (
                            <CreditNoteCounterSelect
                                fieldName={fieldNames.creditNoteCounterId}
                                labelTextKey={TextKey.CreditNoteCounter}
                                billType={option.fromNullable(bill?.type)}
                            />
                        ) : null}
                    </Stack>
                )}
                <BillDetails billId={billId} />
                <ActionLogInfo billId={billId} />
            </Stack>
        </FloatingPanel>
    );
};
